import {
    CardItem,
    CollectionEvent,
    createCardClickedEvent,
    isPublicationCardItem,
    toLinkWithHint,
    useFeature,
    useOnScreen,
} from '@news-mono/web-common'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import React, { useRef } from 'react'
import {
    StyledCTAButton,
    StyledCardImage,
    StyledCardImageContainer,
    StyledContentCard,
    StyledDotLogo,
    StyledLinkWrapper,
    StyledLiveLabel,
    StyledLiveStatus,
    StyledText,
    StyledTextContainer,
    StyledTimeToRead,
} from './ContentCard.styled'
import {
    EditorialType,
    getEditorialType,
} from '../../../templates/Publication/lib/get-editorial-type'

export type CardType = 'large' | 'medium' | 'medium-video-hub' | 'small'

interface CTAProps {
    title: string
    link?: string
}

export interface ContentCardProps {
    item: MaybeLoaded<CardItem>
    cardNumber: number
    cardType: CardType
    onEvent: (event: CollectionEvent) => void
    gridAreaName?: string
    displayRightSeparator?: boolean
    cta?: CTAProps
    shouldHideTeaser?: boolean
}

const getImageWidth = (cardType: CardType) => {
    switch (cardType) {
        case 'small':
            return '350px'
        case 'medium':
            return '740px'
        case 'medium-video-hub':
            return '740px'
        case 'large':
            return '740px'
    }
}

export const ContentCard: React.FC<ContentCardProps> = ({
    item,
    cardNumber,
    cardType,
    onEvent,
    gridAreaName,
    displayRightSeparator,
    cta,
    shouldHideTeaser,
}) => {
    function linkClicked(
        e: React.MouseEvent<HTMLElement>,
        item: MaybeLoaded<CardItem>,
        cardNumber: number,
    ) {
        if (!item.loaded) {
            e.preventDefault()
            return
        }
        onEvent(
            createCardClickedEvent(
                onEvent,
                item.result,
                'InternalPortrait',
                'Portrait',
                cardNumber,
                'InlineRelatedCollection',
            ),
        )
    }

    const cardRef = useRef<HTMLDivElement>(null)
    const isVisible = useOnScreen(cardRef)
    const storyClassification = useFeature('story-classification')
    const editorialType = item.loaded
        ? getEditorialType(item.result, storyClassification)
        : undefined

    if (item.loaded && isPublicationCardItem(item.result)) {
        const timeToRead = item?.result?.readTimeMinutes
            ? `${Math.ceil(item.result.readTimeMinutes)} min read`
            : ''

        return (
            <StyledLinkWrapper
                to={toLinkWithHint(item)}
                onClick={(event) => {
                    linkClicked(event, item, cardNumber)
                }}
                gridAreaName={gridAreaName}
            >
                <StyledContentCard
                    ref={cardRef}
                    cardType={cardType}
                    displayRightSeparator={displayRightSeparator}
                >
                    <StyledTextContainer cardType={cardType}>
                        <StyledText
                            item={{
                                loaded: true,
                                result: item.result,
                            }}
                            fontScale={1}
                            onEvent={onEvent}
                            teaserMode={
                                cardType === 'small' || shouldHideTeaser
                                    ? 'hidden'
                                    : 'visible'
                            }
                            kickerMode={'hidden'}
                        />
                        {editorialType &&
                        editorialType === EditorialType.Live ? (
                            <StyledLiveStatus>
                                <StyledDotLogo />
                                <StyledLiveLabel>Live</StyledLiveLabel>
                            </StyledLiveStatus>
                        ) : (
                            timeToRead &&
                            !cta && (
                                <StyledTimeToRead>
                                    {timeToRead}
                                </StyledTimeToRead>
                            )
                        )}

                        {cta && <StyledCTAButton>{cta.title}</StyledCTAButton>}
                    </StyledTextContainer>
                    <StyledCardImageContainer cardType={cardType}>
                        <StyledCardImage
                            item={{
                                loaded: true,
                                result: item.result,
                            }}
                            imageWidths={{
                                mobile: getImageWidth(cardType),
                                tablet: getImageWidth(cardType),
                                desktop: getImageWidth(cardType),
                                fallbackWidth: 740,
                            }}
                            hasBackground={true}
                            onEvent={onEvent}
                            mediaMode={'default'}
                            fixedRatio={'16:9'}
                            disableImageLazyLoad={isVisible}
                            displayNoScriptImage={true}
                            isVideoPage={cardType === 'medium-video-hub'}
                        />
                    </StyledCardImageContainer>
                </StyledContentCard>
            </StyledLinkWrapper>
        )
    } else {
        return null
    }
}
